import { IoClose } from 'react-icons/io5';
import { toast } from 'react-toastify';
import { useState, useCallback, useMemo } from 'react';
import { Avatar, Divider, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Button, useDisclosure } from '@ds';
import { Channel } from 'stream-chat';
import { ShareToChatModalProps } from '../types';
import { WarningModal } from './WarningModal';
import {
  CHAT_ERROR_MESSAGES,
  CHAT_LABEL_CANCEL,
  CHAT_LABEL_LUNGS_ON_OCS_WARNING,
  CHAT_LABEL_SHARE_MODAL_BUTTON_FALLBACK,
  CHAT_LABEL_SHARE_MODAL_SENDING,
  CHAT_LABEL_SHARE_MODAL_TITLE,
  CHAT_LABEL_UNKNOWN_CHAT,
  CustomMessageField,
  SHARED_DATA_HEADER,
} from '../constants';
import { shouldSkipWarningModal } from '../utils';
import { sanitizeAttachmentForChats } from '../utils/shareToChat.utils';

import { ClinicalItemType, OrganType } from '@/api/apis/case';
import { StreamChannelType } from '@/api/apis/chat';

export const ShareToChatModal = ({
  open,
  channels,
  onClose,
  sharedContent,
  modalProps,
  sendMessage,
}: ShareToChatModalProps) => {
  const { organType, caseStatus, donorType } = modalProps;

  const [selectedChat, setSelectedChat] = useState<Channel | null>(null);
  const [sendingMessageId, setSendingMessageId] = useState<string | null>(null);
  const { isOpen: isWarningModalOpen, onOpen: openWarningModal, onClose: closeWarningModal } = useDisclosure();

  const [warningMode, setWarningMode] = useState<'general' | 'lungsOnOcs' | null>(null);

  const messagePayload = useMemo(
    () => ({
      text: SHARED_DATA_HEADER,
      [CustomMessageField.ClinicalDataAttachment]: sanitizeAttachmentForChats(
        organType,
        sharedContent?.clinicalItemType,
        sharedContent?.clinicalDataAttachment,
      ),
    }),
    [organType, sharedContent?.clinicalItemType, sharedContent?.clinicalDataAttachment],
  );

  const handleShareToChannel = useCallback(
    async (channel: Channel) => {
      if (!sharedContent || sendingMessageId === channel.id) return;

      const isGeneralChat = channel.type === StreamChannelType.General;
      const { clinicalItemType } = sharedContent;

      const skipWarning = shouldSkipWarningModal(clinicalItemType);
      const isLungsOnOcsVideo = organType === OrganType.Lungs && clinicalItemType === ClinicalItemType.OnOCSVideo;

      const showWarning = isGeneralChat && (!skipWarning || isLungsOnOcsVideo);

      if (showWarning) {
        setSelectedChat(channel);
        setWarningMode(isLungsOnOcsVideo ? 'lungsOnOcs' : 'general');
        openWarningModal();
        return;
      }

      await sendMessage(channel, messagePayload);
      onClose();
    },
    [
      caseStatus,
      donorType,
      messagePayload,
      onClose,
      openWarningModal,
      organType,
      sendMessage,
      sendingMessageId,
      sharedContent,
    ],
  );

  const handleConfirmShare = useCallback(async () => {
    try {
      if (!selectedChat) return;
      setSendingMessageId(selectedChat.id ?? null);

      await sendMessage(selectedChat, messagePayload);

      closeWarningModal();
      onClose();
    } catch (err) {
      toast.error(CHAT_ERROR_MESSAGES.failedToShare);
    } finally {
      setSendingMessageId(null);
      setWarningMode(null);
    }
  }, [selectedChat, sendMessage, messagePayload, closeWarningModal, onClose]);

  const handleCloseWarning = useCallback(() => {
    closeWarningModal();
    setWarningMode(null);
  }, [closeWarningModal]);

  return (
    <>
      <Modal
        className="scroll flex items-center justify-center"
        scrollBehavior="outside"
        isOpen={open}
        onOpenChange={onClose}
        closeButton={<IoClose size={42} className="z-1000" />}
        classNames={{
          closeButton: 'text-base-10 mt-6 mr-4',
          header: 'text-hl! flex flex-col gap-4 text-base-20 font-secondary pt-6 pb-4',
          body: 'flex flex-col mx-6 p-0 pb-6 gap-6 shadow-inset-l overflow-y-auto max-h-[400px]',
          footer: 'pb-6',
        }}
      >
        <ModalContent className="max-w-[500px] h-[500px]">
          <ModalHeader className="text-xl font-semibold text-gray-900">
            <div>{CHAT_LABEL_SHARE_MODAL_TITLE}</div>
          </ModalHeader>

          <ModalBody className="overflow-y-auto max-h-[400px] px-4">
            <Divider />
            <div>
              {!channels.length ? (
                <p>No open chats found.</p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {channels.map((ch) => (
                    <li key={ch.id} className="py-2 flex gap-2 items-center">
                      <Avatar
                        className="shrink-0"
                        size="sm"
                        src={typeof ch.data?.image === 'string' ? ch.data.image : undefined}
                      />
                      <Button
                        onClick={() => handleShareToChannel(ch)}
                        variant="outline"
                        size="md"
                        isDisabled={sendingMessageId === ch.id}
                      >
                        {sendingMessageId === ch.id
                          ? CHAT_LABEL_SHARE_MODAL_SENDING
                          : (ch.data?.name ?? CHAT_LABEL_SHARE_MODAL_BUTTON_FALLBACK(ch.id || CHAT_LABEL_UNKNOWN_CHAT))}
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose} variant="solid" size="md" className="bg-black text-white rounded-full px-6 py-3">
              {CHAT_LABEL_CANCEL}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {selectedChat && (
        <WarningModal
          isOpen={isWarningModalOpen}
          onClose={handleCloseWarning}
          onConfirm={handleConfirmShare}
          chatName={selectedChat?.data?.name ?? StreamChannelType.General}
          bodyText={warningMode === 'lungsOnOcs' ? CHAT_LABEL_LUNGS_ON_OCS_WARNING : undefined}
        />
      )}
    </>
  );
};
